import React from 'react';
import { Star, Package, Plus, Minus } from 'lucide-react';
import { ResponsiveImage } from './ResponsiveImage';
import { getEffectiveUnitPrice, getSalePercent } from '../utils/pricing';
import { getProductLabelColor, getProductLabelText } from '../utils/productLabel';
import { useVocab } from '../hooks/useVocab';

export const ProductCard = ({
    product,
    onAddToCart,
    onRemoveFromCart,
    quantity,
    onDetails,
    rtl = true,
    categoryName = null,
    onRequestPaidOptions = null, // callback to open paid options modal
}) => {
    const t = useVocab();
    const hasQuantity = quantity > 0;
    const rating = product.rating; // optional
    // Effective stock = min of product-level and category-level inventory (whichever are enabled).
    // Either limit can independently cause out-of-stock / cap behavior.
    const productInvEnabled = product.inventory?.enabled;
    const categoryInvEnabled = product.category_inventory?.enabled;
    const parseStock = (v) => {
        if (v === undefined || v === null || v === '') return undefined;
        const n = parseInt(v, 10);
        return isNaN(n) ? undefined : n;
    };
    const productStock = productInvEnabled ? parseStock(product.inventory?.quantity ?? product.stock_quantity)
                                            : parseStock(product.stock_quantity);
    const categoryStock = categoryInvEnabled ? parseStock(product.category_inventory?.quantity) : undefined;
    const limits = [productStock, categoryStock].filter((n) => typeof n === 'number');
    const stock = limits.length > 0 ? Math.min(...limits) : undefined;
    const inventoryEnabled = productInvEnabled || categoryInvEnabled;
    const isOutOfStock = !!product.sold_out || (inventoryEnabled && typeof stock === 'number' && stock <= 0);
    const canAddMore = !inventoryEnabled || stock === undefined || quantity < stock;
    // Custom admin-defined label (text + scheme color) shown over the image.
    const labelText = getProductLabelText(product);
    const labelColor = getProductLabelColor(product.label?.color);

    return (
        <div
            tabIndex={0}
            role="group"
            data-card-root
            onKeyDown={(e) => { if (e.key === 'Enter') onDetails(product); }}
            className="relative flex flex-col rounded-lg bg-white shadow-soft hover:shadow-elevated focus:shadow-elevated transition-all duration-300 overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-mekomit-400/50 cursor-pointer group"
            onClick={() => onDetails(product)}
            dir={rtl ? 'rtl' : 'ltr'}
        >
            {/* Media */}
            <div className="relative overflow-hidden">
                {product.images && product.images.length > 0 ? (
                    <div className="group/image overflow-hidden aspect-square">
                        <ResponsiveImage
                            src={product.images[0]}
                            alt={product.name}
                            aspectRatio="aspect-square"
                            lazy={true}
                            className="transition-transform duration-500 ease-smooth group-hover/image:scale-105"
                        />
                    </div>
                ) : (
                    <div className="relative aspect-square bg-gradient-to-br from-surface-muted to-mekomit-50 w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-mekomit-300/60" />
                    </div>
                )}
                {/* Out of stock overlay */}
                {isOutOfStock && (
                    <div className="absolute inset-0 bg-white/75 backdrop-blur-sm flex items-center justify-center">
                        <span className="bg-danger-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-md">
                            אזל המלאי
                        </span>
                    </div>
                )}
                {/* Category badge - top left */}
                {categoryName && (
                    <div className="absolute top-2 left-2 bg-mekomit-500/90 backdrop-blur-sm text-white px-2 py-0.5 rounded-md text-[10px] font-semibold shadow-sm z-10">
                        {categoryName}
                    </div>
                )}
                {/* Top-right stack: quantity badge (in-cart) + custom admin label */}
                {(hasQuantity || labelText) && (
                    <div className="absolute top-2 right-2 flex flex-col items-end gap-1 z-10">
                        {hasQuantity && (
                            <div className="bg-success-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                                {quantity}
                            </div>
                        )}
                        {labelText && (
                            <span className={`${labelColor.badgeClass} backdrop-blur-sm px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm max-w-[8rem] truncate`}>
                                {labelText}
                            </span>
                        )}
                    </div>
                )}
                {/* Sale banner */}
                {product.sale?.enabled && product.sale?.percent > 0 && (
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-r from-danger-600 to-danger-500 text-white text-center py-1 px-2 shadow-md z-10">
                        <span className="text-[11px] font-bold tracking-wide">
                            במבצע · {product.sale.percent}% הנחה
                        </span>
                    </div>
                )}
            </div>

            {/* Body - Compact info section */}
            <div data-card-body className="flex flex-1 flex-col p-2.5 gap-1.5">
                {/* Name */}
                <h3 className="text-xs font-medium text-ink-strong line-clamp-2 leading-snug">
                    {product.name}
                </h3>

                {/* Price and Rating */}
                <div className="flex items-center justify-between gap-1">
                    {getSalePercent(product) > 0 ? (
                        <div className="flex items-baseline gap-1.5">
                            <span className="text-danger-600 font-bold text-sm">₪{getEffectiveUnitPrice(product).toFixed(2)}</span>
                            <span className="text-[10px] line-through text-ink-subtle">₪{product.price}</span>
                        </div>
                    ) : (
                        <span className="text-mekomit-700 font-bold text-sm">₪{product.price}</span>
                    )}
                    {rating !== undefined && rating !== null && (
                        <div className="flex items-center gap-0.5">
                            <Star className="w-3 h-3 text-warning-500 fill-warning-500" />
                            <span className="text-[10px] font-semibold text-ink-muted">{Number(rating).toFixed(1)}</span>
                        </div>
                    )}
                </div>

                {/* Quick action button */}
                {!isOutOfStock && (
                    <div className="mt-auto pt-1">
                        {!hasQuantity ? (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Check if product has paid options and callback is provided
                                    const hasPaidOptions = product.paid_options && product.paid_options.length > 0;
                                    if (hasPaidOptions && onRequestPaidOptions) {
                                        onRequestPaidOptions(product);
                                    } else {
                                        onAddToCart(product);
                                    }
                                }}
                                className="w-full bg-mekomit-500 hover:bg-mekomit-600 active:bg-mekomit-700 text-white font-medium rounded-md py-1.5 text-xs shadow-sm hover:shadow transition-all duration-250"
                                aria-label={t.addToCart}
                            >
                                {t.addToCart}
                            </button>
                        ) : (
                            <div className="flex items-center justify-between bg-mekomit-50 border border-mekomit-200 rounded-md px-1.5 py-1">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onRemoveFromCart(product.product_id); }}
                                    className="w-6 h-6 flex items-center justify-center rounded bg-danger-500 hover:bg-danger-600 active:bg-danger-700 text-white transition-colors duration-200"
                                    aria-label="הפחת כמות"
                                >
                                    <Minus className="w-3 h-3" />
                                </button>
                                <span className="font-bold text-ink-strong text-xs px-1.5">{quantity}</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (canAddMore) {
                                            // Check if product has paid options and callback is provided
                                            const hasPaidOptions = product.paid_options && product.paid_options.length > 0;
                                            if (hasPaidOptions && onRequestPaidOptions) {
                                                onRequestPaidOptions(product);
                                            } else {
                                                onAddToCart(product);
                                            }
                                        }
                                    }}
                                    disabled={!canAddMore}
                                    className={`w-6 h-6 flex items-center justify-center rounded transition-colors duration-200 ${
                                        canAddMore
                                            ? 'bg-success-600 hover:bg-success-700 active:bg-success-800 text-white'
                                            : 'bg-surface-sunken text-ink-subtle cursor-not-allowed'
                                    }`}
                                    aria-label="הוסף כמות"
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductCard;